import {
  Text,
  Input as ChakraInput,
  InputProps as ChakraInputProps,
  Select,
} from "@chakra-ui/react";
import {
  ChangeEvent,
  FocusEvent,
  forwardRef,
  useEffect,
  useState,
} from "react";
import { useFormContext } from "react-hook-form";

export type InputSelectOption = {
  value: string;
  label: string;
};

const InputSelect = forwardRef<
  HTMLInputElement,
  ChakraInputProps & { name: string; options: InputSelectOption[] }
>(
  (
    { children, placeholder, onChange, onBlur, onFocus, options, ...props }, ref
  ) => {
    const [value, setValue] = useState(props.defaultValue);
    const [active, setActive] = useState(false);
    const { watch, register } = useFormContext();
    const watcher = watch(props.name);
    useEffect(() => {
      if (watcher != null && watcher !== value) {
        setValue(watcher);
        setActive(watcher !== "");
      }
    }, [watcher, value, setValue, setActive]);

    useEffect(() => {
      if (props.defaultValue !== undefined) {
        setActive(true);
      }
    }, [props.defaultValue]);

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      setValue(event.target.value);
      setActive(event.target.value !== "");
      if (onChange) {
        onChange(event);
      }
    };

    const handleBlur = (event: FocusEvent<HTMLInputElement, Element>) => {
      if (value === "") {
        setActive(false);
      } else {
        setActive(true);
      }
      if (onBlur) {
        onBlur(event);
      }
    };

    const handleFocus = (event: FocusEvent<HTMLInputElement, Element>) => {
      setActive(true);
      if (onFocus) {
        onFocus(event);
      }
    };

    return (
      <>
        <ChakraInput
          ref={ref}
          id={props.id}
          type={props.type}
          step={props.step}
          min={props.min}
          max={props.max}
          name={props.name}
          isDisabled={props.isDisabled}
          onFocus={handleFocus}
          onBlur={handleBlur}
          value={value}
          onChange={handleChange}
        >
          {children}
        </ChakraInput>
        <Text
          position="absolute"
          top="0%"
          left="5px"
          transform={
            active
              ? "translate(10px,-10px) scale(0.8)"
              : "translate(10px,8px) scale(1)"
          }
          color="gray.500"
          px={1}
          transformOrigin="top left"
          transition="all .2s ease-out"
          pointerEvents="none"
          bg="white"
          w="fit-content"
          h="fit-content"
          zIndex={5}
        >
          {placeholder}
        </Text>
        <Select
          w={"fit-content"}
          position={"absolute"}
          top="0%"
          right="0%"
          border={"none"}
          color={props.color}
          {...register(`${props.id}-select`)}
        >
          {options &&
            options.map((opt) => (
              <option value={opt.value}>{opt.label}</option>
            ))}
        </Select>
      </>
    );
  }
);

export default InputSelect;
